import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Password strength validation
  const checkPasswordStrength = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&#]/.test(password)
    };
  };

  const passwordStrength = checkPasswordStrength(formData.password);
  const isPasswordValid = Object.values(passwordStrength).every(v => v);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password strength
    if (!isPasswordValid) {
      setError('Password does not meet the required strength criteria');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.resetPassword(token, { password: formData.password });
      
      // Show success message and redirect to login
      alert(response.data.message);
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '440px', margin: '40px auto' }}>
        <h2 style={{ marginBottom: '8px', textAlign: 'center', color: '#667eea', fontSize: '22px', fontWeight: '700' }}>
          Reset Password
        </h2>
        <p style={{ textAlign: 'center', color: '#718096', fontSize: '13px', marginBottom: '24px' }}>
          Enter your new password below
        </p>
        
        {error && (
          <div className="error" style={{ marginBottom: '16px' }}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder="Enter new password"
                style={{ 
                  paddingRight: '45px',
                  borderColor: formData.password && !isPasswordValid ? '#f56565' : undefined
                }}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#718096',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {/* Password strength indicator */}
            {(passwordFocused || formData.password) && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f7fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                  Password must contain:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <PasswordRequirement 
                    met={passwordStrength.minLength} 
                    text="At least 8 characters"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasUppercase} 
                    text="One uppercase letter (A-Z)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasLowercase} 
                    text="One lowercase letter (a-z)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasNumber} 
                    text="One number (0-9)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasSpecialChar} 
                    text="One special character (@$!%*?&#)"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label>Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                style={{ paddingRight: '45px' }}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#718096',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', marginTop: '8px', padding: '12px' }}
            disabled={loading || (formData.password && !isPasswordValid)}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>
        
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link 
            to="/login" 
            style={{ 
              color: '#667eea', 
              fontSize: '13px', 
              fontWeight: '600', 
              textDecoration: 'none' 
            }}
          >
            ← Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

// Password requirement indicator component
function PasswordRequirement({ met, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        fontSize: '16px',
        color: met ? '#48bb78' : '#cbd5e0',
        fontWeight: 'bold'
      }}>
        {met ? '✓' : '○'}
      </span>
      <span style={{ color: met ? '#2d3748' : '#718096' }}>
        {text}
      </span>
    </div>
  );
}

export default ResetPassword;
